import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { authService } from "@/services/supabase/auth";
import { useForm } from "react-hook-form";

interface ProfileSettingsFormProps {
  isLoading?: boolean;
  isSubmitting?: boolean;
  error?: boolean;
  onRetry?: () => void;
  noCard?: boolean;
}

interface EmailFormData {
  email: string;
}

const ProfileSettingsForm: React.FC<ProfileSettingsFormProps> = ({
  error: propsError,
  onRetry,
  noCard = false,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<EmailFormData>({
    defaultValues: {
      email: user?.email || "",
    },
  });

  // Set initial email value when user is loaded
  React.useEffect(() => {
    if (user?.email) {
      setValue("email", user.email);
    }
  }, [user?.email, setValue]);

  const onSubmit = async (data: EmailFormData) => {
    if (!user) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: updateError } = await authService.updateEmail(data.email);
      
      if (updateError) {
        setError(updateError);
        toast({
          title: "Error updating email",
          description: updateError,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email update initiated",
          description: "Please check your new email address for a confirmation link.",
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update email";
      setError(errorMessage);
      toast({
        title: "Error updating email",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <>
      <div className="space-y-1 mb-6">
        <h3 className="text-lg font-semibold">Change Email</h3>
        <p className="text-sm text-muted-foreground">Manage your email address</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <p className="text-sm font-medium text-destructive">
                {errors.email.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              This email is used for account notifications and password resets. You will need to confirm your new email address after changing it.
            </p>
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <Button type="submit" disabled={isSubmitting} className="hover:bg-black/90 disabled:hover:bg-black">
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </form>
    </>
  );

  // Show error alert if there's an error
  if (error || propsError) {
    const errorContent = (
      <>
        <div className="space-y-1 mb-6">
          <h3 className="text-lg font-semibold">Email Settings</h3>
          <p className="text-sm text-muted-foreground">Manage your email address</p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || "Failed to load profile settings"}
          </AlertDescription>
        </Alert>
        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={onRetry || (() => setError(null))}>
            Retry
          </Button>
        </div>
      </>
    );

    if (noCard) {
      return errorContent;
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Email Settings</CardTitle>
          <CardDescription>Manage your email address</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error || "Failed to load profile settings"}
            </AlertDescription>
          </Alert>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={onRetry || (() => setError(null))}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (noCard) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Email</CardTitle>
        <CardDescription>Manage your email address</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <p className="text-sm font-medium text-destructive">
                {errors.email.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              This email is used for account notifications and password resets. You will need to confirm your new email address after changing it.
            </p>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between">
          <Button type="button" variant="outline">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="hover:bg-black/90 disabled:hover:bg-black">
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default React.memo(ProfileSettingsForm);
